import {ResourceAllocationState, Person} from '../../types';

/**
 * Pure helpers that mirror the database's ON DELETE CASCADE behaviour in local
 * client state. The server only broadcasts the top-level XDeleted event (e.g.
 * ProjectDeleted) and relies on the DB to cascade children; clients must prune
 * those children themselves or they linger as orphans (e.g. allocations that
 * still count toward a person's load) until a full refetch.
 *
 * Used by both the SignalR handlers (other clients) and the optimistic delete
 * actions (the originating client) so the two can never drift apart.
 */

const omitWhere = <T>(
    map: Record<string, T>,
    shouldRemove: (value: T) => boolean
): Record<string, T> =>
    Object.fromEntries(
        Object.entries(map).filter(([, value]) => !shouldRemove(value))
    );

/**
 * Remove a deleted team/role id from every person's embedded teamIds/roleIds
 * array. Team/role memberships are not separate client-state maps — they live on
 * the person object — so deleting a team/role must scrub the dangling reference.
 * Returns the same map reference when nothing changed to avoid needless re-renders.
 */
const stripIdFromPeople = (
    people: Record<string, Person>,
    key: 'teamIds' | 'roleIds',
    id: string
): Record<string, Person> => {
    let changed = false;
    const next: Record<string, Person> = {};
    for (const [personId, person] of Object.entries(people)) {
        const ids = person[key];
        if (ids && ids.includes(id)) {
            changed = true;
            next[personId] = {...person, [key]: ids.filter(x => x !== id)};
        } else {
            next[personId] = person;
        }
    }
    return changed ? next : people;
};

/**
 * Project → phases, assignments, allocations (of those assignments),
 * project-tags and project-people. (DB: Project cascades phases/assignments/
 * projectTags/projectPeople; Assignment cascades allocations.)
 */
export const removeProjectCascade = (
    state: ResourceAllocationState,
    projectId: string
): ResourceAllocationState => {
    const {[projectId]: _removedProject, ...projects} = state.projects;

    const removedAssignmentIds = new Set(
        Object.values(state.assignments)
            .filter(a => a.projectId === projectId)
            .map(a => a.id)
    );

    return {
        ...state,
        projects,
        phases: omitWhere(state.phases, p => p.projectId === projectId),
        assignments: omitWhere(
            state.assignments,
            a => a.projectId === projectId
        ),
        allocations: omitWhere(state.allocations, al =>
            removedAssignmentIds.has(al.assignmentId)
        ),
        projectTags: omitWhere(
            state.projectTags,
            pt => pt.projectId === projectId
        ),
        // Assignment-tags of the removed assignments go too (DB cascades them via
        // the assignment).
        assignmentTags: omitWhere(state.assignmentTags, at =>
            removedAssignmentIds.has(at.assignmentId)
        ),
        projectPeople: omitWhere(
            state.projectPeople,
            pp => pp.projectId === projectId
        )
    };
};

/** Assignment → allocations. (DB: Assignment cascades allocations.) */
export const removeAssignmentCascade = (
    state: ResourceAllocationState,
    assignmentId: string
): ResourceAllocationState => {
    const {[assignmentId]: _removedAssignment, ...assignments} =
        state.assignments;

    return {
        ...state,
        assignments,
        allocations: omitWhere(
            state.allocations,
            al => al.assignmentId === assignmentId
        ),
        assignmentTags: omitWhere(
            state.assignmentTags,
            at => at.assignmentId === assignmentId
        )
    };
};

/**
 * Person → project-people, personal availability and availability weeks.
 * (DB: Person cascades personalAvailability/personalAvailabilityWeek and the
 * person-team / person-job-role join rows, which the client keeps embedded on
 * the person object rather than as separate maps. ProjectPerson is Restrict, but
 * we prune it defensively to match the optimistic delete action.)
 */
export const removePersonCascade = (
    state: ResourceAllocationState,
    personId: string
): ResourceAllocationState => {
    const {[personId]: _removedPerson, ...people} = state.people;

    return {
        ...state,
        people,
        projectPeople: omitWhere(
            state.projectPeople,
            pp => pp.personId === personId
        ),
        personTags: omitWhere(state.personTags, pt => pt.personId === personId),
        personalAvailability: omitWhere(
            state.personalAvailability,
            pa => pa.personId === personId
        ),
        personalAvailabilityWeeks: omitWhere(
            state.personalAvailabilityWeeks,
            paw => paw.personId === personId
        )
    };
};

/**
 * Team → person memberships. (DB: Team cascades PersonTeam; the client keeps
 * memberships as person.teamIds, so scrub the deleted id from each person.)
 */
export const removeTeamCascade = (
    state: ResourceAllocationState,
    teamId: string
): ResourceAllocationState => {
    const {[teamId]: _removedTeam, ...teams} = state.teams;

    return {
        ...state,
        teams,
        people: stripIdFromPeople(state.people, 'teamIds', teamId),
        teamTags: omitWhere(state.teamTags, tt => tt.teamId === teamId)
    };
};

/**
 * Job role → person memberships. (DB: JobRole cascades PersonJobRole; the client
 * keeps memberships as person.roleIds, so scrub the deleted id from each person.)
 */
export const removeRoleCascade = (
    state: ResourceAllocationState,
    roleId: string
): ResourceAllocationState => {
    const {[roleId]: _removedRole, ...roles} = state.roles;

    return {
        ...state,
        roles,
        people: stripIdFromPeople(state.people, 'roleIds', roleId)
    };
};

/**
 * Tag → project-tags, person-tags, assignment-tags. (Permanent delete is refused
 * server-side while a tag is in use, so in practice these are already empty; we
 * prune defensively to mirror the optimistic delete action.)
 */
export const removeTagCascade = (
    state: ResourceAllocationState,
    tagId: string
): ResourceAllocationState => {
    const {[tagId]: _removedTag, ...tags} = state.tags;

    return {
        ...state,
        tags,
        projectTags: omitWhere(state.projectTags, pt => pt.tagId === tagId),
        personTags: omitWhere(state.personTags, pt => pt.tagId === tagId),
        assignmentTags: omitWhere(
            state.assignmentTags,
            at => at.tagId === tagId
        ),
        teamTags: omitWhere(state.teamTags, tt => tt.tagId === tagId)
    };
};
